import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {CommunicationService} from '../../../services/communication.service';
import {TranslateService} from '@ngx-translate/core';
import {PreferencesService} from '../../../services/preferences.service';
import {Router} from '@angular/router';
import {Helper} from '../../../classes/helper';
import {Filters} from '../../../classes/user-preferences';
import {ListingsService} from '../../../services/listings.service';
import {FiltersCommercialComponent} from './filters-commercial.component';
import {SelectOption} from '../../forms/select/select.component';
import {UpdatedSince} from "../../../classes/enums";

@Component({
  selector: 'app-filters-commercial-desktop',
  templateUrl: 'filters-commercial.desktop.component.html',
  styleUrls: ['../filters.desktop.component.scss', 'filters-commercial.desktop.component.scss']
})
export class FiltersCommercialDesktopComponent extends FiltersCommercialComponent implements AfterViewInit, OnInit {
  showPrice: boolean;
  showMore: boolean;
  minSqftPriceOptions: SelectOption[] = [];
  maxSqftPriceOptions: SelectOption[] = [];
  @ViewChild('form') formElementRef: ElementRef;

  constructor(
    protected preferencesService: PreferencesService,
    protected communicationService: CommunicationService,
    protected translateService: TranslateService,
    protected router: Router,
    protected listingsService: ListingsService,
    protected fb: FormBuilder,
    protected cd: ChangeDetectorRef
  ) {
    super(preferencesService, communicationService, translateService, router, listingsService, fb, cd);
    const listingTypesControls = this.filtersForm.controls['listingTypes'] as FormGroup;
    this.listingType.forEach((it) => {
      listingTypesControls.addControl(it, this.fb.control(false));
    });

  }

  ngOnInit() {
    super.ngOnInit();

    this.minSqftPriceOptions = this.pricePerSqftValues.map((value) => new SelectOption('$' + Helper.formatPrice(value), value));
    this.maxSqftPriceOptions = this.pricePerSqftValues.map((value) => new SelectOption('$' + Helper.formatPrice(value), value));

    this.minSqftPriceOptions[0].key = 'filters.no-min';
    this.maxSqftPriceOptions[0].key = 'filters.no-max';
  }

  @HostListener('window:click', ['$event'])
  onPageClick($event: MouseEvent) {
    // TODO: this is triggering when you switch between luxury and residential and the form is reset, which causes the dropdowns to close when toggling a property type
    // Hide all the dropdowns if the dropdowns are open and they click something not a dropdown
    if ((this.showAllListingTypes || this.showAllBusinessTypes || this.showMore) && !this.formElementRef.nativeElement.contains($event.target)) {
      this.closeDropdowns();
    }
  }

  protected _resetForm() {
    this.clearListingTypes();
    this.clearBusinessTypes();
    this.filtersForm.get('minPrice').patchValue(0);
    this.filtersForm.get('maxPrice').patchValue(0);
  }

  broadcastChangedFilters() {
    const value = Object.assign(new Filters(), this.filtersForm.value);
    if (this.isPricePerSqft) {
      value.pricePerSqFt.min = this.filtersForm.get('minPrice').value;
      value.pricePerSqFt.max = this.filtersForm.get('maxPrice').value;
      value.price = {};
    } else {
      value.price.min = this.filtersForm.get('minPrice').value;
      value.price.max = this.filtersForm.get('maxPrice').value;
      value.pricePerSqFt = {};
    }
    this._preferencesService.updateFilters(value);
    this.communicationService.searchParametersChanged.next();
  }

  updateMaxPriceOptions() {
    const minValue = this.filtersForm.controls['minPrice'].value;
    const maxValue = this.filtersForm.controls['maxPrice'].value;
    // Reset the max value in case the min value is greater
    if (minValue > maxValue) {
      this.filtersForm.controls['maxPrice'].setValue(0);
    }
    // Disable the max options if the min value is greater than the option
    if (this.isPricePerSqft) {
      this.maxSqftPriceOptions.forEach((option) => {
        option.disabled = minValue > option.value && option.value !== 0;
        return option
      });
    } else {
      this.maxPriceOptions = this.maxPriceOptions.map((option) => {
        option.disabled = minValue > option.value && option.value !== 0;
        return option;
      });
    }
    this.cd.detectChanges();
  }

  /**
   * Close all dropdowns, and set the status of one dropdown to the desired status
   * @param dropdownName dropdownName we want to specifically toggle
   * @param newStatus the new status
   */
  closeDropdowns(dropdownName?, newStatus?) {
    this.showListingTypes = false;
    this.showBusinessTypes = false;
    this.showPrice = false;
    this.showMore = false;
    // this.showSize = false;

    if (dropdownName) this[dropdownName] = newStatus;
  }

  onSubmit() {
    this.closeDropdowns();
  }

  get propertyText(): string {
    const totalPropsEnabled = Object.keys(this.listingTypesFg.value).filter((key) => this.listingTypesFg.value[key]).length;
    return totalPropsEnabled > 0 ? `Property Types (${totalPropsEnabled})` : 'All Property Types';
  }

  get businessText(): string {
    if (!this.businessType.length) return '';
    const totalPropsEnabled = Object.keys(this.businessTypesFg.value).filter((key) => this.businessTypesFg.value[key]).length;
    return totalPropsEnabled > 0 ? `Business Types (${totalPropsEnabled})` : 'All Business Types';
  }

  get priceText(): string {
    const minPrice = this.filtersForm.controls['minPrice'].value;
    const maxPrice = this.filtersForm.controls['maxPrice'].value;
    // TODO: Translations
    if (minPrice === 0 && maxPrice === 0) return 'Any Price';
    else if (minPrice > 0 && maxPrice === 0) return Helper.toDollarMagnitudes(minPrice) + '+';
    else if (minPrice === 0 && maxPrice > 0) return Helper.toDollarMagnitudes(0) + ' - ' + Helper.toDollarMagnitudes(maxPrice);
    else return Helper.toDollarMagnitudes(minPrice) + ' - ' + Helper.toDollarMagnitudes(maxPrice);
  }

  get moreText(): string {
    let totalExtraValuesEnabled = 0;
    if (this.filtersForm.get('stories').value.min || this.filtersForm.get('stories').value.max) totalExtraValuesEnabled++;
    if (this.filtersForm.get('size').value.min || this.filtersForm.get('size').value.max) totalExtraValuesEnabled++;
    if (this.filtersForm.get('units').value.min || this.filtersForm.get('units').value.max) totalExtraValuesEnabled++;
    if (this.filtersForm.get('totalAcres').value.min || this.filtersForm.get('totalAcres').value.max) totalExtraValuesEnabled++;
    if (this.filtersForm.get('parking').value.min || this.filtersForm.get('parking').value.max) totalExtraValuesEnabled++;
    if (this.filtersForm.get('updatedSince').value && this.filtersForm.get('updatedSince').value != UpdatedSince.ANY) totalExtraValuesEnabled++;
    // TODO: translation
    return 'More' + (totalExtraValuesEnabled > 0 ? ` (${totalExtraValuesEnabled})` : '');
  }

}

